import * as Sentry from '@sentry/browser'
import { getWindow } from './global'

export default {
  addListener,
  removeListener,
  addMetaListener,
  removeMetaListener,
}

export enum KeyAction {
  down = 'down',
  up = 'up',
  bottom = 'bottom',
  goToLeastTimer = 'goToLeastTimer',
  search = 'search',
  quickAction = 'quickAction',
  filterShowings = 'filterShowings',
  message = 'message',
  editMessage = 'editMessage',
  lobby = 'lobby',
  inbox = 'inbox',
  tasks = 'tasks',
  editName = 'editName',
  toggleArchive = 'toggleArchive',
  archive1 = 'archive1',
  togglePricing = 'togglePricing',
  enter = 'enter',
  esc = 'esc',
  toggleUnitTypeStudio = 'toggleUnitTypeStudio',
  toggleUnitTypeBR1 = 'toggleUnitTypeBR1',
  toggleUnitTypeBR2 = 'toggleUnitTypeBR2',
  toggleUnitTypeBR3 = 'toggleUnitTypeBR3',
  toggleUnitTypeBR4 = 'toggleUnitTypeBR4',
  toggleUnitTypeBR5 = 'toggleUnitTypeBR5',
  toggleVirtualTour = 'toggleVirtualTour',
  toggleInPersonTour = 'toggleInPersonTour',
  toggleSelfGuidedTour = 'toggleSelfGuidedTour',
}

export interface KeyActionArguments {
  showPricingTitle?: string
  showPricingTaskId?: number
}

const listeners: ((action: KeyAction, args?: KeyActionArguments) => void)[] = []
let lastActionKey: string | null = null

/**
 * Binds the function you provide to the entire page, listens for any meta keypress.
 *
 * ... like  `ctrl + c` or `cmd + v`
 */
function addMetaListener(listener: (e: KeyboardEvent) => void) {
  getWindow()!.addEventListener('keydown', listener)
}

function removeMetaListener(listener: (e: KeyboardEvent) => void) {
  getWindow()!.removeEventListener('keydown', listener)
}

/**
 * Binds a function you provide to a single keypress of a Dynasty-enumerated keyboard shortcut.
 *
 * e.g. 'enter' or 'down', but not 'cmd + c'
 */
function addListener(listener: (action: KeyAction, args?: KeyActionArguments) => void) {
  listeners.push(listener)
}

function removeListener(listener: (action: KeyAction, args?: KeyActionArguments) => void) {
  const ix = listeners.indexOf(listener)
  if (ix >= 0) {
    listeners.splice(ix, 1)
  } else {
    Sentry.captureException(new Error('Tried to remove a keyboard listener, listener was not found'))
  }
}

export function fire(action: KeyAction, args?: KeyActionArguments) {
  listeners.forEach(l => l(action, args))
}

getWindow()!.addEventListener('keydown', (e: KeyboardEvent) => {
  let action = null as KeyAction | null
  const unitTypeKeyMap: { [index: string]: KeyAction } = {
    0: KeyAction.toggleUnitTypeStudio,
    1: KeyAction.toggleUnitTypeBR1,
    2: KeyAction.toggleUnitTypeBR2,
    3: KeyAction.toggleUnitTypeBR3,
    4: KeyAction.toggleUnitTypeBR4,
    5: KeyAction.toggleUnitTypeBR5,
  }
  if (e.ctrlKey && unitTypeKeyMap.hasOwnProperty(e.key)) {
    fire(unitTypeKeyMap[e.key])
    return
  }
  if (
    e.target &&
    (e.target as HTMLElement).tagName &&
    ['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)
  ) {
    if (e.key === 'Escape') {
      action = KeyAction.esc
    } else {
      // most keyboard shortcuts are not active while in an input
      return
    }
  }

  action = actionFromKey(e.key) || action

  if (action != null) {
    fire(action)
    lastActionKey = e.key
  }
})

function actionFromKey(key: string): KeyAction | undefined {
  switch (key) {
    case 'j':
      return KeyAction.down
    case 'k':
      return KeyAction.up
    case 'b':
      return KeyAction.bottom
    case 'g':
      return KeyAction.goToLeastTimer
    case 's':
      return KeyAction.search
    case 'q':
      return KeyAction.quickAction
    case 'f':
      return KeyAction.filterShowings
    case 'm':
      return KeyAction.message
    case 'e':
      return KeyAction.editMessage
    case 'l':
      return KeyAction.lobby
    case 'i':
      return KeyAction.inbox
    case 't':
      return KeyAction.tasks
    case 'n':
      return KeyAction.editName
    case 'y':
      return KeyAction.toggleArchive
    case '1':
      return KeyAction.archive1
    case 'p':
      return KeyAction.togglePricing
    case 'V':
      return KeyAction.toggleVirtualTour
    case 'I':
      return KeyAction.toggleInPersonTour
    case 'S':
      return KeyAction.toggleSelfGuidedTour
    case 'Escape':
      return KeyAction.esc
    default:
      return undefined
  }
}

/**
 * Prevent double-execution of keyboard shortcuts
 */
getWindow()!.addEventListener('keypress', e => {
  if (lastActionKey === e.key) {
    e.preventDefault()
  }
  lastActionKey = null
})
